import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';

/**
 * Helper function to format date
 */
export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Calculate due date based on invoice date and due amount
 * If due amount is 0 (fully paid), return invoice date
 * Otherwise, return due date or invoice date + 30 days
 */
export const calculateDueDate = (invoiceDate, dueDate, dueAmount) => {
  // If fully paid (due amount is 0), due date is same as invoice date
  if (dueAmount === 0) {
    return invoiceDate;
  }
  
  // If due date is provided, use it
  if (dueDate) {
    return dueDate;
  }
  
  // Otherwise, calculate as invoice date + 30 days
  const invoiceDateObj = new Date(invoiceDate);
  invoiceDateObj.setDate(invoiceDateObj.getDate() + 30);
  return invoiceDateObj.toISOString();
};

/**
 * Fill invoice template with data
 */
export const getFilledTemplate = (templateHtml, invoice, storeInfo, t = (key) => key) => {
  const lineItems = invoice.LineItems || [];
  const totalQuantity = lineItems.reduce((sum, item) => sum + Number(item.Quantity || 0), 0);
  
  // ✅ Use backend-provided invoice totals - no calculation needed
  const vatPercentage = Number(invoice.VATPercentage || 21);
  const totalAmount = Number(invoice.InvoiceTotalAmount || 0);
  const paidAmount = Number(invoice.InvoicePaidAmount || 0);
  const dueAmount = Number(invoice.InvoiceDueAmount || (totalAmount - paidAmount));
  const totalExcl = Number(invoice.InvoiceSubTotal || 0);
  const totalVAT = Number(invoice.InvoiceTotalVATAmount || 0);
  const discountAmount = Number(invoice.InvoiceDiscount || 0);

  // Extract customer data from Customer object
  const customer = invoice.Customer || {};
  const customerName = customer.Name || invoice.CustomerName || '';
  const customerVAT = customer.VATNumber || invoice.CustomerVAT || '';
  const customerAddress = customer.Street || invoice.CustomerAddress || '';
  const customerCity = customer.City || invoice.CustomerCity || '';
  const customerPostCode = customer.PostCode || '';
  const customerCountry = customer.Country || '';
  const customerEmail = customer.Email || '';
  const customerPhone = customer.PhoneNumber || customer.MobilePhone || '';

  // Get invoice date from first line item's CreatedDate
  const invoiceDate = lineItems.length > 0 && lineItems[0].CreatedDate 
    ? lineItems[0].CreatedDate 
    : invoice.InvoiceDate || invoice.CreatedDate || new Date().toISOString();

  // ✅ Use backend-provided PaymentDueDate (fallback to DueDate if not available)
  const dueDate = invoice.PaymentDueDate || invoice.DueDate || invoiceDate;

  // Determine payment status
  let paymentStatus = 'Not Paid';
  if (paidAmount >= totalAmount) paymentStatus = 'Paid';
  else if (paidAmount > 0 && paidAmount < totalAmount) paymentStatus = 'Partially Paid';

  // Build payment methods section for multiple payments
  let paymentMethodsSection = '';
  if (invoice.Payments && invoice.Payments.length > 0 && paidAmount > 0) {
    const paymentMethodsList = invoice.Payments
      .map(pm => `<div class="total-row"><span>${pm.PaymentMethodName || pm.PaymentMethod?.Name || 'Payment'}:</span><strong>€${Number(pm.Amount || 0).toFixed(2)}</strong></div>`)
      .join('');
    paymentMethodsSection = paymentMethodsList;
  } else if (paidAmount > 0 && invoice.PaymentMethod && !invoice.PaymentMethod.includes('/')) {
    paymentMethodsSection = `<div class="total-row"><span>Payment Method:</span><strong>${invoice.PaymentMethod}</strong></div>`;
  } else {
    paymentMethodsSection = `<div class="total-row"><span>Payment Method:</span><strong>N/A</strong></div>`;
  }

  // Generate product rows
  const ProductRows = lineItems.map(item => {
    const itemTotalExcl = Number(item.TotalExcl || 0);
    // Use stored TotalIncl if available, otherwise calculate
    const itemTotalIncl = item.TotalIncl ? Number(item.TotalIncl) : itemTotalExcl * (1 + vatPercentage / 100);
    const itemVatAmount = itemTotalIncl - itemTotalExcl;
    
    return `
    <tr>
      <td>${item.UpdatedProductName || item.updatedProductName || item.ProductName || item.Description || item.Name || ''}</td>
      <td class="text-center">${item.Quantity || 0}</td>
      <td class="text-right">€${Number(item.UnitPrice || item.Price || 0).toFixed(2)}</td>
      <td class="text-right">€${itemTotalExcl.toFixed(2)}</td>
      <td class="text-right">€${itemVatAmount.toFixed(2)}</td>
      <td class="text-right">€${itemTotalIncl.toFixed(2)}</td>
    </tr>
  `;
  }).join('');

  // Generate VAT section
  const VATSection = vatPercentage > 0
    ? `
    <div>
      <span>VAT ${vatPercentage}%: €${totalExcl.toFixed(2)}</span>
      <span>€${totalVAT.toFixed(2)}</span>
    </div>
    `
    : `<div><span>VAT 0%:</span><span>€0.00</span></div>`;

  // Replace all placeholders
  let filledTemplate = templateHtml
    // Store Info - Using fetched store data
    .replace(/{{CompanyName}}/g, storeInfo?.name || 'Your Store Name')
    .replace(/{{CompanyVAT}}/g, storeInfo?.vat || '')
    .replace(/{{CompanyEmail}}/g, storeInfo?.email || '')
    .replace(/{{CompanyPhone}}/g, storeInfo?.phone || '')
    .replace(/{{CompanyStreetAddress}}/g, storeInfo?.address || '')
    .replace(/{{CompanyCityCountry}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' ') || '')
    .replace(/{{CompanyBankDetail}}/g, storeInfo?.iban || '')
    .replace(/{{CompanyIBAN}}/g, storeInfo?.iban || '')
    
    // Customer Info - Using Customer object
    .replace(/{{CustomerName}}/g, customerName)
    .replace(/{{CustomerVAT}}/g, customerVAT)
    .replace(/{{CustomerStreetAddress}}/g, customerAddress)
    .replace(/{{CustomerCityCountry}}/g, [customerPostCode, customerCity, customerCountry].filter(Boolean).join(' '))
    .replace(/{{CustomerEmail}}/g, customerEmail)
    .replace(/{{CustomerPhone}}/g, customerPhone)
    .replace(/{{CustomerCountry}}/g, customerCountry)
    
    // Invoice Info - Using line item's CreatedDate
    .replace(/{{InvoiceNumber}}/g, invoice.InvoiceNumber || '')
    .replace(/{{InvoiceDate}}/g, formatDate(invoiceDate))
    .replace(/{{DueDate}}/g, formatDate(dueDate))
    .replace(/{{PaymentMethod}}/g, invoice.PaymentMethod || 'CASH')
    .replace(/{{PaymentMethodsSection}}/g, paymentMethodsSection)
    
    // Product Info
    .replace(/{{ProductRows}}/g, ProductRows)
    .replace(/{{TotalQuantity}}/g, totalQuantity.toString())
    .replace(/{{DiscountRow}}/g, discountAmount > 0 ? `<tr class="discount-row"><td colspan="5" style="text-align:right; font-style: italic;">Discount:</td><td class="text-right">- €${discountAmount.toFixed(2)}</td></tr>` : '')
    
    // Financial Totals
    .replace(/{{TotalExcl}}/g, totalExcl.toFixed(2))
    .replace(/{{TotalVAT}}/g, totalVAT.toFixed(2))
    .replace(/{{TotalAmount}}/g, totalAmount.toFixed(2))
    .replace(/{{TotalIncl}}/g, totalAmount.toFixed(2))
    .replace(/{{GrandTotal}}/g, totalAmount.toFixed(2))
    .replace(/{{InvoicePaidAmount}}/g, paidAmount.toFixed(2))
    .replace(/{{InvoiceDueAmount}}/g, dueAmount.toFixed(2))
    
    // VAT Section
    .replace(/{{VATSection}}/g, VATSection)
    .replace(/{{VAT}}/g, vatPercentage.toString())
    
    // Payment Status
    .replace(/{{PaymentStatus}}/g, paymentStatus)
    
    // Peppol Info
    .replace(/{{PeppolStatus}}/g, invoice.PeppolStatus || 'Not Sent')
    .replace(/{{PeppolMessageId}}/g, invoice.PeppolMessageId || '')
    
    // Other placeholders
    .replace(/{{OutstandingSection}}/g, '')
    .replace(/{{CustomTextSection}}/g, invoice.CustomText ? `<div style="margin-top: 20px; padding: 15px; background: #f9f9f9; border-left: 4px solid #667eea; border-radius: 5px;"><strong style="color: #667eea;">Additional Notes:</strong><br><div style="margin-top: 8px; line-height: 1.6;">${invoice.CustomText}</div></div>` : '')
    .replace(/{{PageNumber}}/g, '1')
    .replace(/{{TotalPages}}/g, '1');

  return filledTemplate;
};

/**
 * Generate and preview PDF using html2canvas and jsPDF
 */
export const generateAndPreviewPDF = async (invoice, storeInfo, templatePath = '/invoicetemplates/template-en.html', t = (key) => key) => {
  try {
    // Check if store info is loaded
    if (!storeInfo) {
      console.error('Store info not available:', { storeInfo });
      toast.error(t('store_info_not_loaded') || 'Store information not loaded. Please refresh the page.');
      return;
    }

    // Fetch template
    const templateHtml = await fetch(templatePath).then((res) => res.text());
    const filledTemplate = getFilledTemplate(templateHtml, invoice, storeInfo, t);

    // Create temporary div
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = filledTemplate;
    
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '794px';
    tempDiv.style.background = 'white';
    document.body.appendChild(tempDiv);

    // Wait for images to load
    const images = tempDiv.getElementsByTagName('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(), 3000);
        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        img.onerror = () => {
          clearTimeout(timeout);
          resolve();
        };
      });
    });

    await Promise.all(imagePromises);

    // Generate canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      logging: false,
      width: 794,
      windowWidth: 794,
      backgroundColor: '#ffffff',
      onclone: (clonedDoc) => {
        clonedDoc.body.style.background = 'white';
      }
    });

    document.body.removeChild(tempDiv);

    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Invalid canvas generated');
    }

    // Create PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    if (imgHeight > pdfHeight) {
      const scaledWidth = (canvas.width * pdfHeight) / canvas.height;
      const scaledHeight = pdfHeight;
      pdf.addImage(imgData, 'PNG', 0, 0, scaledWidth, scaledHeight);
    } else {
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    }

    // Open in new tab
    const pdfBlob = pdf.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
    
    return true;
  } catch (error) {
    console.error('❌ PDF generation error:', error);
    
    if (error.message.includes('Invalid canvas')) {
      toast.error(t('failed_to_render_invoice') || 'Failed to render invoice');
    } else if (error.message.includes('html2canvas')) {
      toast.error(t('failed_to_convert_html') || 'Failed to convert HTML');
    } else {
      toast.error(t('failed_to_generate_pdf') || 'Failed to generate PDF. Please try again.');
    }
    return false;
  }
};
